import { useLanguage } from '../LanguageContext';
import React, { useState, useEffect, useCallback } from 'react';
import '../App.css';
import BanModal from './BanModal';
import DetailsModal from './DetailsModal';
import DataTable from './DataTable';
import axiosInstance from '../axiosInstance';
import {
  Container,
  Typography,
  Select,
  MenuItem,
  Button,
  Snackbar,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

const PlayerList = () => {
  const { t } = useLanguage();
  const [players, setPlayers] = useState([]);
  const [days, setDays] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [showBanModal, setShowBanModal] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [playerBans, setPlayerBans] = useState([]);
  const [banDialogOpen, setBanDialogOpen] = useState(false);
  const navigate = useNavigate();

  const fetchPlayers = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get(`/api/get_players/${days}`, {
        withCredentials: true,
      });

      console.log("📡 API Request URL:", `/get_players/${days}`);
      console.log("📥 Full API Response:", response.data);

      if (!Array.isArray(response.data.players)) {
        console.error("⚠️ Unexpected API response format:", response.data);
        throw new Error(t("Invalid response format"));
      }

      setPlayers(response.data.players);
    } catch (err) {
      console.error("❌ Error fetching players:", err);
      const errorMessage = err.response?.data?.error || err.message || t("Unknown error occurred.");
      setSnackbarMessage(t("❌ Error fetching players: {error}", { error: t(errorMessage) }));
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  }, [days, t]);

  useEffect(() => {
    console.log("🔍 Checking authentication...");
    axiosInstance.get("/api/check-auth", { withCredentials: true })
      .then(response => {
        if (response.data.success) {
          console.log("✅ User authenticated:", response.data);
        } else {
          console.warn("⚠️ User authentication failed. Redirecting to login...");
          navigate("/login");
        }
      })
      .catch(err => {
        console.error("❌ Error checking auth:", err);
        navigate("/login");
      });

    fetchPlayers();
  }, [fetchPlayers, navigate, days]);

  const handleKickPlayer = async (player) => {
    try {
      const response = await axiosInstance.post(
        "/api/kick_player",
        { player_uid: player.player_identity },
        { withCredentials: true }
      );

      setSnackbarMessage(response.data.message || t("✅ Player {name} kicked successfully.", { name: player.player_name }));
      setSnackbarOpen(true);
    } catch (err) {
      setSnackbarMessage(err.response?.data?.error || t("❌ Failed to kick player {name}.", { name: player.player_name }));
      setSnackbarOpen(true);
    }
  };

  const handleDaysChange = (event) => {
    setDays(Number(event.target.value));
  };

  const handleBanClick = (player) => {
    setSelectedPlayer(player);
    setShowBanModal(true);
  };

  const handleCloseModal = () => {
    setShowBanModal(false);
    setSelectedPlayer(null);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleBanSaved = (data) => {
    setSnackbarMessage(data.message || t("Player banned successfully."));
    setSnackbarOpen(true);
    fetchPlayers();
  };

  // FIXED: store selected player + use /api/get_bans_by_identity
  const handlePlayerNameClick = async (player) => {
    try {
      setSelectedPlayer(player);
      const response = await axiosInstance.get(
        `/api/get_bans_by_identity/${player.player_identity}`,
        { withCredentials: true }
      );
      setPlayerBans(response.data || []);
      setBanDialogOpen(true);
    } catch (err) {
      console.error("❌ Failed to fetch ban history:", err);
      setSnackbarMessage(err.response?.data?.error || t("Failed to fetch ban history."));
      setSnackbarOpen(true);
    }
  };

  const handleCloseBanDialog = () => {
    setBanDialogOpen(false);
    setPlayerBans([]);
  };

  return (
    <Container maxWidth={false} sx={{ px: { xs: 2, md: 4 }, py: 4 }}>
      <Typography variant="h3" component="h1" gutterBottom>{t("Player directory")}</Typography>

      <Typography color="text.secondary" sx={{ mb: 3 }}>{t("Find players, review their history, and manage access to the servers.")}</Typography>
      <div className="page-controls">
        <Select size="small" value={days} onChange={handleDaysChange} inputProps={{ 'aria-label': t("Player activity period") }}>
          {[1, 2, 3, 4, 5, 6, 7].map(day => <MenuItem key={day} value={day}>{t("Last {count} day(s)", { count: day })}</MenuItem>)}
        </Select>
        <Button variant="outlined" disabled={isLoading} onClick={fetchPlayers}>{t("Refresh players")}</Button>
      </div>
      <DataTable key={days} label="players" rows={players} loading={isLoading} rowKey={player => player.player_identity} columns={[
        { key: 'player_name', label: t("Player name"), render: player => <Button onClick={() => handlePlayerNameClick(player)}>{player.player_name || t("Unknown")}</Button> },
        { key: 'player_identity', label: t("Player ID"), mono: true },
        { key: 'actions', label: t("Actions"), searchable: false, render: player => <div className="row-actions">
          <Button variant="outlined" color="error" onClick={() => handleBanClick(player)}>{t("Ban")}</Button>
          <Button variant="outlined" color="warning" onClick={() => handleKickPlayer(player)}>{t("Kick")}</Button>
        </div> },
      ]} />

      <DetailsModal
        open={banDialogOpen}
        onClose={handleCloseBanDialog}
        playerBans={playerBans}
        selectedPlayerName={selectedPlayer?.player_name || t("Unknown")}
        onUnban={async (playerId, playerName) => {
          try {
            const response = await axiosInstance.post(
              "/api/unban_player",
              { identity: playerId, player_name: playerName },
              { withCredentials: true }
            );
            if (response.data?.success) {
              setSnackbarMessage(response.data.message || t("✅ Unbanned successfully."));
              setSnackbarOpen(true);
              const r = await axiosInstance.get(`/api/get_bans_by_identity/${playerId}`, { withCredentials: true });
              setPlayerBans(r.data);
            } else {
              setSnackbarMessage(response.data?.message || t("Unban may have failed."));
              setSnackbarOpen(true);
            }
          } catch (err) {
            setSnackbarMessage(err.response?.data?.error || t("❌ Failed to unban player."));
            setSnackbarOpen(true);
          }
        }}
      />

      {selectedPlayer && (
        <BanModal
          open={showBanModal}
          onClose={handleCloseModal}
          playerData={selectedPlayer}
          onSaved={handleBanSaved}
        />
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        message={t(snackbarMessage)}
      />
    </Container>
  );
};

export default PlayerList;
